/**
 * Hook to fetch and cache OpenAPI schema
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../api/client';

interface OpenAPISchema {
  paths: Record<string, any>;
  components: {
    schemas: Record<string, any>;
  };
}

export function useOpenAPISchema() {
  const [schema, setSchema] = useState<OpenAPISchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchSchema();
  }, []);

  const fetchSchema = async () => {
    try {
      const response = await axios.get<OpenAPISchema>(`${API_BASE_URL}/openapi.json`);
      setSchema(response.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch OpenAPI schema'));
      console.error('Failed to fetch OpenAPI schema:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get the request body schema for a specific task type endpoint
   */
  const getTaskSchema = (taskType: string): any | null => {
    if (!schema || !schema.paths) return null;

    // Try to find the endpoint for this task type
    // Format: /api/{category}/{task_type}
    const possiblePaths = [
      `/api/analysis/${taskType}`,
      `/api/testing/${taskType}`,
      `/api/authoring/${taskType}`,
      `/api/chat/${taskType}`,
      `/api/translation/${taskType}`,
      `/api/similarity/${taskType}`,
    ];

    for (const path of possiblePaths) {
      const endpoint = schema.paths?.[path];
      if (endpoint?.post?.requestBody) {
        const contentSchema = endpoint.post.requestBody.content?.['application/json']?.schema;
        if (contentSchema) {
          // Resolve $ref if present
          if (contentSchema.$ref) {
            const refPath = contentSchema.$ref.split('/');
            const schemaName = refPath[refPath.length - 1];
            return schema.components?.schemas?.[schemaName] || null;
          }
          return contentSchema;
        }
      }
    }

    return null;
  };

  return {
    schema,
    loading,
    error,
    getTaskSchema,
  };
}
